import { Anchor, Badge, Button, Card, Collapse, Divider, Group, Skeleton, Stack, Text, Title } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { Fragment, useState } from 'react';
import { Link } from 'react-router';
import type { ClientReleasedDossierDto } from '@mexp/shared';
import { ErrorState } from '@/components/States';
import { FileList } from '@/features/files/FileList';
import { useClientOptions, useClientReleases } from './api';
import { ReleaseFiles } from './ReleaseFiles';
import { fullDate, stageLabel, STAGE_COLOR } from './stage';

/**
 * One dossier's current release, ready to open or download, then the corrected documents Makin sent as compliance on
 * the client's queries, with any versions before it folded underneath.
 */
function ReleasedDossier({ d }: { d: ClientReleasedDossierDto }) {
  const [latest, ...earlier] = d.releases;
  const [showEarlier, setShowEarlier] = useState(false);
  if (!latest) return null;
  const reopened = d.stage !== 'completed';
  return (
    <Stack gap="xs">
      <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
        <Stack gap={2} style={{ minWidth: 0 }}>
          <Anchor component={Link} to={`/requests/${d.id}`} c="inherit" underline="hover" fw={600} fz="md" lh={1.3}>
            {d.productName}{' '}
            <Text span c="dimmed" fw={400} inherit>
              for {d.country}
            </Text>
          </Anchor>
          <Text fz="xs" c="dimmed" className="tabular">
            {d.code} · version {latest.version}, approved {fullDate(latest.releasedAt.slice(0, 10))}
          </Text>
        </Stack>
        {reopened && (
          <Badge color={STAGE_COLOR[d.stage]} variant="light" size="md">
            {d.stage === 'cancelled' ? stageLabel(d.stage) : 'Query open'}
          </Badge>
        )}
      </Group>
      {latest.changeNote && (
        <Text fz="sm" c="dimmed">
          Includes the changes you asked for: “{latest.changeNote}”
        </Text>
      )}
      <ReleaseFiles release={latest} />
      {d.compliances.map((c) => (
        <Stack key={c.query} gap={0} pt={6}>
          <Text fz="xs" c="dimmed" className="tabular">
            Compliance on Query {c.query} · sent {fullDate(c.sentAt.slice(0, 10))}
          </Text>
          <ReleaseFiles release={{ files: c.files, link: null }} />
        </Stack>
      ))}
      {earlier.length > 0 && (
        <Stack gap="xs">
          <Button
            variant="subtle"
            size="compact-sm"
            w="fit-content"
            px={0}
            aria-expanded={showEarlier}
            leftSection={<IconChevronDown size={14} style={{ transform: showEarlier ? 'rotate(180deg)' : undefined, transition: 'transform 150ms ease-out' }} />}
            onClick={() => setShowEarlier((v) => !v)}
          >
            {showEarlier ? 'Hide earlier versions' : earlier.length === 1 ? `Show version ${earlier[0]?.version}` : `Show ${earlier.length} earlier versions`}
          </Button>
          <Collapse in={showEarlier} transitionDuration={150}>
            <Stack gap="md" pt={4}>
              {earlier.map((v) => (
                <Stack key={v.version} gap={6}>
                  <Text fz="xs" c="dimmed" className="tabular">
                    Version {v.version} · approved {fullDate(v.releasedAt.slice(0, 10))}
                  </Text>
                  {v.changeNote && (
                    <Text fz="xs" c="dimmed">
                      Changed because you asked: “{v.changeNote}”
                    </Text>
                  )}
                  <FileList files={v.files} />
                </Stack>
              ))}
            </Stack>
          </Collapse>
        </Stack>
      )}
    </Stack>
  );
}

/** Every version Makin released, and the compliance files on the client's queries, across the company's dossiers. */
export function ClientDownloadsPage() {
  const options = useClientOptions();
  const releases = useClientReleases();
  const items = releases.data ?? [];
  const versions = items.reduce((sum, d) => sum + d.releases.length, 0);
  const compliances = items.reduce((sum, d) => sum + d.compliances.length, 0);

  if (releases.isError) return <ErrorState error={releases.error} onRetry={() => void releases.refetch()} />;

  return (
    <Stack gap="md">
      <Stack gap={4}>
        <Title order={1} fz={{ base: 26, sm: 32 }} style={{ letterSpacing: '-0.015em' }}>
          Ready to download
        </Title>
        <Text c="dimmed">
          {options.data?.company.name ?? ' '}
          {releases.data ? ` · ${items.length} dossier${items.length === 1 ? '' : 's'}, ${versions} version${versions === 1 ? '' : 's'} released` : ''}
          {compliances ? `, ${compliances} compliance${compliances === 1 ? '' : 's'}` : ''}
        </Text>
      </Stack>

      <Card p={{ base: 'md', sm: 'xl' }}>
        {releases.isLoading ? (
          <Stack gap="lg">
            <Skeleton h={96} />
            <Skeleton h={96} />
          </Stack>
        ) : items.length ? (
          <Stack gap="lg">
            {items.map((d, i) => (
              <Fragment key={d.id}>
                {i > 0 && <Divider />}
                <ReleasedDossier d={d} />
              </Fragment>
            ))}
          </Stack>
        ) : (
          <Stack gap={6} align="flex-start">
            <Text fw={600}>Nothing to download yet</Text>
            <Text fz="sm" c="dimmed" maw={560}>
              Approved documents appear here as soon as Makin releases them. You can follow each dossier's progress in{' '}
              <Anchor component={Link} to="/requests" fz="sm">
                All dossiers
              </Anchor>
              .
            </Text>
          </Stack>
        )}
      </Card>
    </Stack>
  );
}
